import type {
  AskAnswer,
  BootstrapResponse,
  DecisionAction,
  Feedback,
  OperationalState,
  AttentionDisposition,
} from "@aion/xo-core";
import { getToken } from "firebase/messaging";
import { httpsCallable } from "firebase/functions";

import { auth, functions, messaging } from "./firebase";

const call = async <Input, Output>(name: string, input: Input): Promise<Output> => {
  const callable = httpsCallable<Input, Output>(functions, name);
  const result = await callable(input);
  return result.data;
};

export const bootstrapXO = (timeZone: string) =>
  call<{ timeZone: string }, BootstrapResponse>("bootstrapXO", { timeZone });

export const startConnectorAuth = async (
  provider: "google" | "slack" | "asana" | "notion",
  accountKind: "PERSONAL" | "WORK" | "SCOPED",
): Promise<void> => {
  const result = await call<
    { provider: string; accountKind: string },
    { authorizationUrl: string }
  >("startConnectorAuth", { provider, accountKind });
  window.location.assign(result.authorizationUrl);
};

export const syncConnector = (sourceAccountId: string) =>
  call("syncConnector", { sourceAccountId });

export const disconnectConnector = (sourceAccountId: string) =>
  call("disconnectConnector", { sourceAccountId });

export const purgeConnectorData = (sourceAccountId: string) =>
  call("purgeConnectorData", { sourceAccountId, confirmation: "PURGE" });

export type NotionWebhookBootstrapStatus =
  | "NOT_PREPARED"
  | "PENDING"
  | "CAPTURED"
  | "RETRIEVED";

export interface NotionWebhookBootstrapResult {
  status: NotionWebhookBootstrapStatus;
  expiresAt: string | null;
  capturedAt?: string | null;
  retrievedAt?: string | null;
  webhookUrl?: string;
  verificationToken?: string;
}

export const manageNotionWebhookBootstrap = (
  action: "PREPARE" | "STATUS" | "RETRIEVE" | "COMPLETE",
) =>
  call<{ action: string }, NotionWebhookBootstrapResult>(
    "notionWebhookBootstrap",
    { action },
  );

export const submitFeedback = (
  attentionObjectId: string,
  kind: Feedback["kind"],
  note?: string,
) => call("submitFeedback", { attentionObjectId, kind, note });

export const recordAttentionDecision = (input: {
  attentionObjectId: string;
  action: DecisionAction;
  nextState: OperationalState;
  nextDisposition: AttentionDisposition;
  rationale?: string;
}) => call("recordAttentionDecision", input);

export interface AnswerSignalResult {
  ok: boolean;
  recorded: boolean;
  alreadyRecorded: boolean;
  message: string;
  warning?: string | null;
  syncRunIds: string[];
}

export const answerSignal = (signalId: string, answer: string, domainName?: string) =>
  call<
    { signalId: string; answer: string; domainName?: string },
    AnswerSignalResult
  >("answerSignal", { signalId, answer, domainName });

export const createDomain = (name: string, description: string) =>
  call("createDomain", { name, description, color: "#344B57", icon: "orbit" });

export const registerForPush = async (): Promise<string> => {
  if (Notification.permission !== "granted") {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") throw new Error("Notification permission was not granted.");
  }
  const instance = await messaging();
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
  if (!instance || !vapidKey) {
    throw new Error("Web push is not configured for this installation.");
  }
  const token = await getToken(instance, { vapidKey });
  await call("registerPushToken", {
    token,
    device: `${navigator.platform} · ${navigator.userAgent.includes("Mobile") ? "Mobile PWA" : "Desktop PWA"}`,
  });
  return token;
};

export interface AskStreamResult extends AskAnswer {
  conversationId: string;
}

export const askXO = async ({
  question,
  conversationId,
  onStatus,
}: {
  question: string;
  conversationId?: string;
  onStatus?: (phase: string) => void;
}): Promise<AskStreamResult> => {
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) throw new Error("Sign in before asking XO.");
  const response = await fetch("/api/ask", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ question, conversationId }),
  });
  if (!response.ok || !response.body) throw new Error("XO could not open the answer stream.");
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let answer: AskStreamResult | null = null;
  while (true) {
    const chunk = await reader.read();
    if (chunk.done) break;
    buffer += decoder.decode(chunk.value, { stream: true });
    const frames = buffer.split("\n\n");
    buffer = frames.pop() || "";
    for (const frame of frames) {
      const event = frame.match(/^event: (.+)$/m)?.[1];
      const rawData = frame.match(/^data: (.+)$/m)?.[1];
      if (!event || !rawData) continue;
      const data = JSON.parse(rawData) as Record<string, unknown>;
      if (event === "status") onStatus?.(String(data.phase || "working"));
      if (event === "error") throw new Error(String(data.message || "XO could not answer."));
      if (event === "answer") answer = data as unknown as AskStreamResult;
    }
  }
  if (!answer) throw new Error("XO returned no answer.");
  return answer;
};
